/**
 * ErrorMessage Component - User-Friendly Error Display with Recovery Actions
 *
 * UX-02: Improve Error Messages and Recovery
 *
 * Features:
 * - User-friendly error messages (no technical jargon)
 * - Actionable recovery buttons
 * - Error codes for support/debugging
 * - Auto-dismiss for non-critical errors
 * - Keyboard accessible (ESC to dismiss)
 * - Screen reader compatible
 *
 * Usage:
 * ```jsx
 * import { ErrorMessage, useErrorHandler } from '@/components/ui/ErrorMessage';
 *
 * // Simple usage
 * <ErrorMessage error="AUTH_001" />
 *
 * // With custom retry
 * <ErrorMessage
 *   error="NET_001"
 *   onRetry={handleRetry}
 *   onDismiss={handleDismiss}
 * />
 *
 * // Using hook
 * const { showError, dismissError } = useErrorHandler();
 * showError('Payment failed', { code: 'PAY_001' });
 * ```
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Flag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertTitle, AlertDescription } from './alert';
import { Button } from './button';
import {
  getErrorMessage,
  getRecoveryActionHandler,
  ERROR_CODES,
} from '@/lib/errorDictionary';

// ==========================================
// ERROR MESSAGE COMPONENT
// ==========================================

/**
 * ErrorMessage Component
 * Displays user-friendly error with recovery actions
 */
export const ErrorMessage = ({
  error,
  className,
  onRetry,
  onDismiss,
  showCode = true,
  autoClose = false,
  autoCloseDuration = 5000,
  compact = false,
  testId = 'error-message',
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  // Get error details from dictionary
  const errorDetails = React.useMemo(() => {
    if (typeof error === 'string') {
      return getErrorMessage(error);
    } else if (error instanceof Error) {
      return getErrorMessage(error.message);
    } else if (error?.code) {
      return getErrorMessage(error.code, error.context);
    }
    return getErrorMessage(ERROR_CODES.GEN_001);
  }, [error]);

  // Auto-close for non-critical errors
  useEffect(() => {
    if (autoClose && errorDetails.severity !== 'error') {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDuration, errorDetails.severity]);

  // Handle keyboard shortcuts (ESC to dismiss, R to retry)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      } else if (e.key === 'r' || e.key === 'R') {
        if (onRetry && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          onRetry();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onRetry]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  const handleReportIssue = useCallback(() => {
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`.toUpperCase();
    const errorContext = {
      id: errorId,
      code: errorDetails.code,
      message: errorDetails.message,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Store in sessionStorage
    try {
      const existingErrors = JSON.parse(sessionStorage.getItem('errorLog') || '[]');
      existingErrors.push(errorContext);
      sessionStorage.setItem('errorLog', JSON.stringify(existingErrors.slice(-10)));
      sessionStorage.setItem('lastErrorId', errorId);
    } catch (e) {
      console.warn('Failed to store error:', e);
    }

    // Track in PostHog
    if (window.posthog) {
      try {
        window.posthog.capture('error_reported', errorContext);
      } catch (e) {
        console.warn('Failed to track in PostHog:', e);
      }
    }

    // Track in Sentry
    if (window.Sentry) {
      try {
        window.Sentry.captureMessage(`User reported error: ${errorDetails.code}`, {
          level: 'info',
          tags: { error_id: errorId, error_code: errorDetails.code }
        });
      } catch (e) {
        console.warn('Failed to track in Sentry:', e);
      }
    }

    // Open support email
    const subject = encodeURIComponent(`Error Report: ${errorId}`);
    const body = encodeURIComponent(
      `Error ID: ${errorId}\n` +
      `Error Code: ${errorDetails.code}\n\n` +
      `Please describe what you were doing when the error occurred:\n\n\n\n` +
      `---\nTechnical Details (do not modify):\n` +
      `Message: ${errorDetails.message}\n` +
      `URL: ${window.location.href}\n` +
      `Timestamp: ${new Date().toISOString()}`
    );
    window.location.href = `mailto:support@wixell.com?subject=${subject}&body=${body}`;
  }, [errorDetails]);

  const handleRecoveryAction = useCallback(
    (action) => {
      switch (action.action) {
        case 'retry':
          onRetry?.();
          break;

        case 'refresh':
          window.location.reload();
          break;

        case 'go-back':
          window.history.back();
          break;

        default:
          if (action.route) {
            navigate(action.route);
          } else if (action.action.startsWith('retry-delayed')) {
            setTimeout(() => {
              onRetry?.();
            }, action.delay || 1000);
          }
      }
    },
    [onRetry, navigate]
  );

  if (!isVisible) return null;

  // Severity icon mapping
  const SeverityIcon = {
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
  }[errorDetails.severity] || AlertCircle;

  // Severity variant mapping
  const variant = errorDetails.severity === 'error' ? 'destructive' : 'default';

  return (
    <Alert
      variant={variant}
      className={cn('relative', className)}
      role="alert"
      aria-live="assertive"
      data-testid={testId}
      data-error-code={errorDetails.code}
    >
      {/* Icon */}
      <SeverityIcon className="h-5 w-5" />

      {/* Close button */}
      {onDismiss && (
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-foreground/50 hover:text-foreground transition-colors"
          aria-label="Dismiss error"
          data-testid="error-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Title and Description */}
      <div className="pr-8">
        <AlertTitle className="flex items-center gap-2 flex-wrap">
          {errorDetails.title}
          {showCode && (
            <code className="text-xs font-mono bg-background/80 dark:bg-background/50 px-2 py-1 rounded font-semibold border">
              {errorDetails.code}
            </code>
          )}
        </AlertTitle>

        <AlertDescription className="mt-2">
          {errorDetails.message}

          {/* Suggestion */}
          {errorDetails.suggestion && !compact && (
            <p className="mt-2 text-sm opacity-90">
              💡 <span className="font-medium">Tip:</span> {errorDetails.suggestion}
            </p>
          )}

          {/* Keyboard shortcuts hint */}
          {!compact && (
            <p className="mt-3 text-xs text-muted-foreground">
              Keyboard shortcuts: Press <kbd className="px-1.5 py-0.5 bg-background border rounded text-foreground font-mono">ESC</kbd> to dismiss
              {onRetry && (
                <>
                  , <kbd className="px-1.5 py-0.5 bg-background border rounded text-foreground font-mono">R</kbd> to retry
                </>
              )}
            </p>
          )}
        </AlertDescription>

        {/* Recovery Actions */}
        {errorDetails.recoveryActions && errorDetails.recoveryActions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {errorDetails.recoveryActions.map((action, index) => (
              <Button
                key={index}
                variant={index === 0 ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleRecoveryAction(action)}
                className="gap-2"
                data-testid={`recovery-action-${action.action}`}
              >
                {action.action === 'retry' && <RefreshCw className="h-3.5 w-3.5" />}
                {action.action === 'go-back' && <ArrowLeft className="h-3.5 w-3.5" />}
                {action.route && <ExternalLink className="h-3.5 w-3.5" />}
                {action.label}
              </Button>
            ))}

            {/* Report Issue Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReportIssue}
              className="gap-2 text-muted-foreground hover:text-foreground"
              data-testid="report-issue-btn"
            >
              <Flag className="h-3.5 w-3.5" />
              Report Issue
            </Button>
          </div>
        )}
      </div>
    </Alert>
  );
};

// ==========================================
// INLINE ERROR COMPONENT
// ==========================================

/**
 * InlineError Component
 * Compact error display for forms and inline contexts
 */
export const InlineError = ({
  error,
  className,
  showIcon = true,
  onDismiss,
}) => {
  const errorDetails = React.useMemo(() => {
    if (typeof error === 'string') {
      return getErrorMessage(error);
    }
    return getErrorMessage(ERROR_CODES.GEN_001);
  }, [error]);

  if (!error) return null;

  return (
    <div
      className={cn(
        'flex items-start gap-2 p-2 rounded-md',
        'bg-red-50 dark:bg-red-900/20',
        'border border-red-200 dark:border-red-900/50',
        'text-sm text-red-700 dark:text-red-300',
        className
      )}
      role="alert"
      aria-live="polite"
      data-testid="inline-error"
    >
      {showIcon && (
        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium">{errorDetails.message}</p>
        {errorDetails.suggestion && (
          <p className="text-xs mt-1 opacity-90">
            💡 {errorDetails.suggestion}
          </p>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// ==========================================
// ERROR TOAST COMPONENT
// ==========================================

/**
 * ErrorToast Component
 * Toast notification for errors
 */
export const ErrorToast = ({
  error,
  onDismiss,
  duration = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const errorDetails = React.useMemo(() => {
    return getErrorMessage(error);
  }, [error]);

  useEffect(() => {
    if (errorDetails.severity !== 'error') {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onDismiss?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, errorDetails.severity, onDismiss]);

  if (!isVisible) return null;

  const bgColor = {
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  }[errorDetails.severity];

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50',
        'max-w-md p-4 rounded-lg shadow-lg',
        bgColor,
        'text-white',
        'animate-in slide-in-from-right'
      )}
      role="alert"
      data-testid="error-toast"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-semibold">{errorDetails.title}</p>
          <p className="text-sm mt-1 opacity-90">{errorDetails.message}</p>
        </div>
        {onDismiss && (
          <button
            onClick={() => {
              setIsVisible(false);
              onDismiss();
            }}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// ==========================================
// ERROR HANDLER CONTEXT & HOOK
// ==========================================

const ErrorContext = createContext(null);

/**
 * ErrorProvider Component
 * Provides global error handling
 */
export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);

  const showError = useCallback((error, options = {}) => {
    const id = Math.random().toString(36).substr(2, 9);
    const errorDetails = getErrorMessage(error);

    setErrors((prev) => [
      ...prev,
      {
        id,
        ...errorDetails,
        ...options,
        timestamp: Date.now(),
      },
    ]);

    // Auto-dismiss after duration for non-critical errors
    if (errorDetails.severity !== 'error') {
      setTimeout(() => {
        dismissError(id);
      }, options.duration || 5000);
    }

    return id;
  }, []);

  const dismissError = useCallback((errorId) => {
    setErrors((prev) => prev.filter((err) => err.id !== errorId));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        errors,
        showError,
        dismissError,
        clearAllErrors,
      }}
    >
      {children}

      {/* Render error toasts */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2" data-testid="error-toast-container">
        {errors.map((error) => (
          <ErrorToast
            key={error.id}
            error={error.code}
            onDismiss={() => dismissError(error.id)}
            duration={error.duration}
          />
        ))}
      </div>
    </ErrorContext.Provider>
  );
};

/**
 * useErrorHandler Hook
 * Access global error handling
 */
export const useErrorHandler = () => {
  const context = useContext(ErrorContext);

  if (!context) {
    // Fallback when used outside provider
    return {
      errors: [],
      showError: (error) => console.error('Error:', error),
      dismissError: () => {},
      clearAllErrors: () => {},
    };
  }

  return context;
};

// ==========================================
// ERROR BOUNDARY WRAPPER
// ==========================================

/**
 * ErrorBoundary Component
 * Catches React errors and displays user-friendly message
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo);

    // Log to error tracking service
    if (window.errorTracker) {
      window.errorTracker.captureException(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <ErrorMessage
            error={ERROR_CODES.GEN_001}
            onRetry={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            showCode={!import.meta.env.PROD}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

// ==========================================
// EXPORTS
// ==========================================

export default ErrorMessage;

export {
  ERROR_CODES,
  getErrorMessage,
  getRecoveryActionHandler,
} from '@/lib/errorDictionary';
